import React, { useEffect, useRef, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import styles from './tabs.module.scss';
import { LinkWithParams } from '../link-with-params/link-with-params';
import { PrimaryLink } from '../buttons/buttons';
import { IEntity } from '@monorepo/adminx/src/modules/models/campaign.model';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { Icon } from '../icon/icon';
import { Tooltip } from '@monorepo/controlled/src/components/tooltip/tooltip/tooltip';
import { Flex } from '../flex/flex';
import { Input } from '../form/input/input';

interface ITab {
	route?: string;
	name: string;
	active?: boolean;
	deletable?: boolean;
	id?: id;
}

interface ITabs {
	tabs?: ITab[];
	showAddTab?: boolean;
	showActive?: boolean;
	activeTabId?: id;
	addTabPlaceholder?: string;
	onTabChange?: (tabId: id) => void;
	addOptions?: IEntity[];
	onAddTab?: (option: IEntity) => void;
	onRemoveTab?: (id: id) => void;
	maxVisibleTabs?: number;
	isPhases?: boolean;
	addDropdownClassName?: string;
}

interface TabProps {
	tab: ITab;
	showActive?: boolean;
	onRemoveTab?: (id: id) => void;
}

const Tab = ({ tab, showActive, onRemoveTab }: TabProps) => (
	<Flex gap="8px">
		{tab.name}

		{!!showActive &&
			(tab.active ? (
				<Tooltip content="Active" isFullWidth={false} key="active">
					<Icon isMFP color="green">
						check-circle
					</Icon>
				</Tooltip>
			) : (
				<Tooltip content="Disabled" isFullWidth={false} key="disabled">
					<Icon isMFP color="red">
						x-circle
					</Icon>
				</Tooltip>
			))}

		{tab.deletable && onRemoveTab && (
			<div
				onClick={e => {
					e.stopPropagation();
					onRemoveTab(tab.id!);
				}}>
				<Icon isMFP>trash-01</Icon>
			</div>
		)}
	</Flex>
);

export const Tabs = ({
	tabs = [],
	activeTabId,
	showAddTab,
	showActive,
	addTabPlaceholder,
	onTabChange,
	addOptions = [],
	onAddTab,
	onRemoveTab,
	maxVisibleTabs,
	isPhases = true,
	addDropdownClassName,
}: ITabs) => {
	const isInRouter = useInRouterContext();
	const currentRoute = isInRouter ? useLocation().pathname : undefined;
	const [showDropdown, setShowDropdown] = useState(false);
	const addTabRef = useRef<HTMLDivElement>(null);
	const [showOverflow, setShowOverflow] = useState(false);
	const [optionName, setOptionName] = useState<string | undefined>(undefined);
	let visibleTabs = maxVisibleTabs ? tabs.slice(0, maxVisibleTabs) : tabs;

	const activeTab = tabs.find(tab => tab.id === activeTabId);

	if (maxVisibleTabs && activeTab && !visibleTabs.includes(activeTab)) {
		visibleTabs = [...visibleTabs.slice(0, maxVisibleTabs - 1), activeTab];
	}

	const overflowRef = useRef<HTMLDivElement>(null);
	const hiddenTabs = tabs.filter(tab => !visibleTabs.includes(tab));
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (addTabRef.current && !addTabRef.current.contains(event.target as Node)) {
				setShowDropdown(false);
			}
			if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) {
				setShowOverflow(false);
			}
		}

		function handleEscape(event: KeyboardEvent) {
			if (event.key === 'Escape') {
				setShowDropdown(false);
				setShowOverflow(false);
			}
		}

		if (showDropdown || showOverflow) {
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleEscape);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [showDropdown, showOverflow]);

	const handleOptionNameChange = (name: string) => {
		setOptionName(name);
	};

	return (
		<React.Fragment>
			{isPhases && addTabPlaceholder && !tabs.length && <p className={styles.placeholder}>Please add a phase</p>}

			<div className={styles.wrapper}>
				{visibleTabs.map(tab => {
					const isActive = activeTabId ? tab.id === activeTabId : tab.route === currentRoute;

					const className = `${styles.tabItem} ${isActive ? styles.active : ''}`;

					if (tab.route) {
						return (
							<LinkWithParams
								key={tab.id ?? tab.name}
								to={tab.route}
								className={className}
								onClick={e => {
									if (tab.id && onTabChange) onTabChange(tab.id);
								}}>
								<Tab tab={tab} showActive={showActive} onRemoveTab={onRemoveTab} />
							</LinkWithParams>
						);
					}

					return (
						<a
							key={tab.id ?? tab.name}
							className={className}
							onClick={e => {
								e.preventDefault();
								if (tab.id && onTabChange) onTabChange(tab.id);
							}}>
							<Tab tab={tab} showActive={showActive} onRemoveTab={onRemoveTab} />
						</a>
					);
				})}

				{hiddenTabs.length > 0 && (
					<div ref={overflowRef} className={styles.addTabWrapper}>
						<button type="button" className={styles.overflowButton} onClick={() => setShowOverflow(value => !value)}>
							+{hiddenTabs.length} more
						</button>

						{showOverflow && (
							<div className={styles.dropdown}>
								{hiddenTabs.map(tab => (
									<div
										key={tab.id ?? tab.name}
										className={styles.dropdownItem}
										onClick={() => {
											setShowOverflow(false);
											if (tab.id && onTabChange) {
												onTabChange(tab.id);
											}
										}}>
										<Tab tab={tab} showActive={showActive} onRemoveTab={onRemoveTab} />
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{addOptions.length > 0 && showAddTab && onAddTab && (
					<div ref={addTabRef} className={styles.addTabWrapper}>
						<PrimaryLink
							icon={'plus'}
							onClick={e => {
								e.preventDefault();
								setShowDropdown(prev => !prev);
							}}
							className={styles.addTabButton}
						/>

						{showDropdown && (
							<div className={`${styles.dropdown} ${addDropdownClassName || ''}`}>
								<Input
									value={optionName}
									onChange={(e: { target: { value: string } }) => handleOptionNameChange(e.target.value)}
									icon="search-sm"
									placeholder="Search"
								/>
								{addOptions
									.filter(option => {
										return option.name.toLowerCase().includes(optionName?.toLowerCase() ?? '') || !optionName?.length;
									})
									.map(option => (
										<div
											key={option.id}
											className={styles.dropdownItem}
											onClick={() => {
												setShowDropdown(false);
												onAddTab(option);
												setOptionName('');
											}}>
											{option.name}
										</div>
									))}
							</div>
						)}
					</div>
				)}
			</div>
		</React.Fragment>
	);
};
